/**
 * PublicReceipt — branded landing page parents see when they tap the
 * `/r/<slug>` link from a WhatsApp message. No login required.
 *
 * Loads metadata from `/api/public/r/<slug>` and renders a clean, mobile-first
 * receipt summary card with a prominent "Download Receipt PDF" CTA and an
 * optional UPI quick-pay row. The actual PDF is streamed by the backend at
 * `/api/public/r/<slug>/pdf`.
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Receipt as ReceiptIcon,
  FilePdf,
  ShieldCheck,
  WarningCircle,
  Printer,
} from "@phosphor-icons/react";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatPeriodMonth(period) {
  if (!period) return null;
  const [y, m] = period.split("-");
  const idx = parseInt(m, 10) - 1;
  if (Number.isNaN(idx) || idx < 0 || idx > 11) return period;
  return `${MONTHS[idx]} ${y}`;
}

function formatDateIST(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function PublicReceipt() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/public/r/${slug}`);
        if (!r.ok) {
          if (r.status === 410) throw new Error("This receipt link has been revoked or has expired.");
          if (r.status === 404) throw new Error("Receipt not found. The link may be incorrect.");
          throw new Error("Unable to load receipt. Please try again later.");
        }
        const j = await r.json();
        if (!cancel) setData(j);
      } catch (e) {
        if (!cancel) setError(e.message || String(e));
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  const theme = data?.school?.theme_color || "#1E3A8A";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-500" data-testid="public-receipt-loading">
          Loading receipt…
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 shadow-sm text-center" data-testid="public-receipt-error">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center mb-3">
            <WarningCircle size={28} weight="duotone" />
          </div>
          <div className="text-base font-semibold text-slate-900">Receipt unavailable</div>
          <div className="mt-1.5 text-sm text-slate-600">{error || "Please contact your school office."}</div>
        </div>
      </div>
    );
  }

  const { school, receipt, student, pdf_url } = data;
  const pdfHref = `${API_BASE}/public${pdf_url}`;
  const period = formatPeriodMonth(receipt.period_month);
  const balance = Math.max(0, Number(receipt.fee_amount || 0) - Number(receipt.amount_paid || 0));
  const cleared = balance <= 0.001;

  return (
    <div
      className="min-h-screen bg-slate-50"
      style={{
        backgroundImage: `linear-gradient(180deg, ${theme}10 0%, transparent 200px)`,
      }}
      data-testid="public-receipt-page"
    >
      <div className="max-w-xl mx-auto px-4 py-6 sm:py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          {school.logo_url ? (
            <img
              src={school.logo_url}
              alt=""
              className="w-11 h-11 rounded-md object-contain bg-white border border-slate-200"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div
              className="w-11 h-11 rounded-md flex items-center justify-center text-white font-bold text-lg shrink-0"
              style={{ background: theme }}
            >
              {(school.name || "S").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-900 truncate" data-testid="public-receipt-school-name">
              {school.name || "School"}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {[school.phone, school.email].filter(Boolean).join(" · ")}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
            <ShieldCheck size={13} weight="fill" /> Verified
          </div>
        </div>

        {/* Receipt card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Top strip */}
          <div className="px-5 py-4 flex items-center justify-between" style={{ background: theme }}>
            <div className="text-white">
              <div className="text-[11px] uppercase tracking-wider opacity-80 flex items-center gap-1.5">
                <ReceiptIcon size={12} weight="fill" /> Fee receipt
              </div>
              <div className="text-lg font-semibold mt-0.5" data-testid="public-receipt-no">
                {receipt.receipt_no}
              </div>
            </div>
            <div className="text-right text-white/90">
              <div className="text-[11px] uppercase tracking-wider opacity-80">Date</div>
              <div className="text-xs font-medium mt-0.5">{formatDateIST(receipt.created_at)}</div>
            </div>
          </div>

          {receipt.is_voided && (
            <div className="bg-red-50 border-b border-red-200 px-5 py-2.5 text-xs text-red-800 font-semibold tracking-wide" data-testid="public-receipt-voided">
              ⚠ This receipt has been voided by the school.
            </div>
          )}

          {/* Body */}
          <div className="p-5 space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Student</div>
              <div className="text-base font-semibold text-slate-900 mt-0.5" data-testid="public-receipt-student">
                {student.name || "—"}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {[
                  student.class_name && `Class ${student.class_name}`,
                  student.admission_no && `Adm No ${student.admission_no}`,
                  student.session_name,
                ].filter(Boolean).join(" · ")}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
              <Row label="Head" value={receipt.head + (period ? `  ·  ${period}` : "")} />
              <Row
                label="Amount paid"
                value={<span className="font-semibold" style={{ color: theme }}>{formatINR(receipt.amount_paid)}</span>}
              />
              <Row label="Mode" value={(receipt.payment_mode || "—").toUpperCase()} />
              {receipt.txn_ref && <Row label="Txn ref" value={receipt.txn_ref} />}
              <Row
                label="Balance"
                value={
                  <span className={cleared ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>
                    {formatINR(balance)}{cleared ? " · cleared" : ""}
                  </span>
                }
              />
            </div>

            {/* Primary CTA */}
            <Button
              onClick={() => window.open(pdfHref, "_blank", "noopener")}
              className="w-full h-12 text-base gap-2"
              style={{ background: theme }}
              data-testid="public-receipt-download-btn"
            >
              <FilePdf size={18} weight="fill" /> Download receipt (PDF)
            </Button>

            <button
              type="button"
              onClick={() => window.print()}
              className="w-full text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1.5"
              data-testid="public-receipt-print-btn"
            >
              <Printer size={13} /> Print this page
            </button>

            {school.upi_id && (
              <div className="text-[11px] text-slate-500 text-center pt-1">
                School UPI: <span className="font-mono">{school.upi_id}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 mt-5 leading-relaxed" data-testid="public-receipt-footer">
          {school.receipt_footer || "Thank you for your payment."}
          <div className="mt-1.5 text-[10px] text-slate-400">
            For any queries, contact the school office.
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="px-4 py-2.5 flex items-baseline justify-between gap-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">{label}</div>
      <div className="text-sm text-slate-900 text-right truncate">{value}</div>
    </div>
  );
}
